/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import useEbt from '@modules/ebt/hook'
import type { ProjectType } from '@modules/ui-types/projects'

type ProjectCpsWizardPayload = {
  project_id: string
  project_type: ProjectType
  current_step: number
  selected_projects_count: number
  is_editing: boolean
}

type ProjectCpsWizardNextStepPayloadSchema = {
  eventType: 'project_cps_wizard_next_step'
  payload: ProjectCpsWizardPayload
}

type ProjectCpsWizardPreviousStepPayloadSchema = {
  eventType: 'project_cps_wizard_previous_step'
  payload: ProjectCpsWizardPayload
}

type ProjectCpsWizardCancelPayloadSchema = {
  eventType: 'project_cps_wizard_cancel'
  payload: ProjectCpsWizardPayload
}

type ProjectCpsWizardSubmitPayloadSchema = {
  eventType: 'project_cps_wizard_submit'
  payload: ProjectCpsWizardPayload
}

type ProjectCpsWizardPayloadSchema =
  | ProjectCpsWizardNextStepPayloadSchema
  | ProjectCpsWizardPreviousStepPayloadSchema
  | ProjectCpsWizardCancelPayloadSchema
  | ProjectCpsWizardSubmitPayloadSchema

export const projectCpsWizardSchemaProps = {
  project_id: {
    description: 'Project ID',
    keywordType: 'keyword' as const,
  },
  project_type: {
    description: 'Project Type',
    keywordType: 'keyword' as const,
  },
  current_step: {
    description: 'Current wizard step',
    keywordType: 'long' as const,
  },
  selected_projects_count: {
    description: 'Number of selected projects',
    keywordType: 'long' as const,
  },
  is_editing: {
    description: 'Whether editing existing configuration',
    keywordType: 'boolean' as const,
  },
}

export const useEbtProjectCpsWizard = () => {
  const reportEbtEvent = useEbt<ProjectCpsWizardPayloadSchema>(
    {
      eventType: 'project_cps_wizard_next_step',
      schemaProps: projectCpsWizardSchemaProps,
    },
    {
      eventType: 'project_cps_wizard_previous_step',
      schemaProps: projectCpsWizardSchemaProps,
    },
    {
      eventType: 'project_cps_wizard_cancel',
      schemaProps: projectCpsWizardSchemaProps,
    },
    {
      eventType: 'project_cps_wizard_submit',
      schemaProps: projectCpsWizardSchemaProps,
    },
  )

  return {
    reportProjectCpsWizardNextStepEbt: (payload: ProjectCpsWizardPayload) =>
      reportEbtEvent('project_cps_wizard_next_step', payload),
    reportProjectCpsWizardPreviousStepEbt: (payload: ProjectCpsWizardPayload) =>
      reportEbtEvent('project_cps_wizard_previous_step', payload),
    reportProjectCpsWizardCancelEbt: (payload: ProjectCpsWizardPayload) =>
      reportEbtEvent('project_cps_wizard_cancel', payload),
    reportProjectCpsWizardSubmitEbt: (payload: ProjectCpsWizardPayload) =>
      reportEbtEvent('project_cps_wizard_submit', payload),
  }
}
